// In-memory activity log. Every interesting thing the app does — files
// processed, folders read, edits saved, errors caught — calls into here
// so the user can either watch it scroll past or save it to a file and
// send it to us when something goes wrong.
//
// Why a hand-rolled buffer instead of console.* + DevTools: we want the
// log visible inside the app (not just to developers), and we want
// "Save report" to bundle exactly what happened in a single JSON file
// the user can attach to an email. The console doesn't give us that.

import { APP_VERSION, BUILD_TIME } from "./version.js";

export type LogLevel = "info" | "warn" | "error";

export interface LogEntry {
  /** ISO-8601 timestamp. */
  ts: string;
  level: LogLevel;
  /** Subsystem name: "loader", "processMonth", "state", "ui", ... */
  source: string;
  message: string;
  /** Optional structured payload. Don't put sensitive PII here (bank
   *  details, account numbers) — the user may share this report with us. */
  details?: Record<string, unknown>;
}

// Buffer cap chosen so a heavy session (process ~50 statements + a few
// dozen edits) fits comfortably without unbounded growth. Older entries
// drop off the front when we exceed the cap.
const MAX_BUFFER = 500;
const buffer: LogEntry[] = [];
const listeners = new Set<() => void>();

export function log(
  level: LogLevel,
  source: string,
  message: string,
  details?: Record<string, unknown>,
): void {
  const entry: LogEntry = {
    ts: new Date().toISOString(),
    level,
    source,
    message,
  };
  if (details !== undefined) entry.details = details;
  buffer.push(entry);
  if (buffer.length > MAX_BUFFER) buffer.shift();
  for (const fn of listeners) {
    try {
      fn();
    } catch (err) {
      // Don't let a buggy listener break the log call.
      // eslint-disable-next-line no-console
      console.error("log listener threw:", err);
    }
  }
}

export const info = (source: string, message: string, details?: Record<string, unknown>) =>
  log("info", source, message, details);
export const warn = (source: string, message: string, details?: Record<string, unknown>) =>
  log("warn", source, message, details);
export const error = (source: string, message: string, details?: Record<string, unknown>) =>
  log("error", source, message, details);

/** Snapshot the current buffer. Caller MUST NOT mutate. */
export function getEntries(): readonly LogEntry[] {
  return buffer;
}

/** Counts per level — useful for the UI badge. */
export function counts(): { info: number; warn: number; error: number } {
  const out = { info: 0, warn: 0, error: 0 };
  for (const e of buffer) out[e.level]++;
  return out;
}

/** Register a callback that fires after each log() call. Returns an
 *  unsubscribe function. Idempotent — calling unsubscribe twice is fine. */
export function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** Wipe the buffer. The UI's "Clear" button uses this. Listeners still
 *  fire so the displayed list goes empty. */
export function clear(): void {
  buffer.length = 0;
  for (const fn of listeners) fn();
}

/** Serialize the buffer + a small envelope (app version, build time,
 *  user agent, generated-at) into a JSON blob the user can download or
 *  copy. The envelope lets us match a bug report to the exact deploy
 *  the user was running without having to ask "what version was that?"
 *  `version` is the short git SHA injected at Vite build time. */
export interface Report {
  generated_at: string;
  app: string;
  version: string;
  built_at: string;
  user_agent: string;
  entries: LogEntry[];
}

export function buildReport(): Report {
  return {
    generated_at: new Date().toISOString(),
    app: "personal-finance-web",
    version: APP_VERSION,
    built_at: BUILD_TIME,
    user_agent:
      typeof navigator !== "undefined" && typeof navigator.userAgent === "string"
        ? navigator.userAgent
        : "n/a",
    entries: [...buffer],
  };
}

/** Save the report to disk. In Chromium-based browsers (which the app
 *  already requires for the File System Access API) this opens a real
 *  save-as picker so the user can choose location + name. Falls back
 *  to the blob+anchor download trick on browsers without the API or
 *  when the user dismisses the picker. */
export async function downloadReport(): Promise<void> {
  if (typeof document === "undefined") {
    throw new Error("downloadReport requires a DOM");
  }
  const report = buildReport();
  const json = JSON.stringify(report, null, 2);
  const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
  const suggestedName = `personal-finance-report-${ts}.json`;

  // Prefer showSaveFilePicker — the user sees an actual chooser dialog
  // and picks where the report lands. Chrome 86+ / Edge 86+.
  const w = window as unknown as {
    showSaveFilePicker?: (opts: {
      suggestedName: string;
      types: Array<{ description: string; accept: Record<string, string[]> }>;
    }) => Promise<{
      createWritable: () => Promise<{
        write: (data: BlobPart) => Promise<void>;
        close: () => Promise<void>;
      }>;
    }>;
  };
  if (typeof w.showSaveFilePicker === "function") {
    try {
      const handle = await w.showSaveFilePicker({
        suggestedName,
        types: [
          {
            description: "Activity log JSON",
            accept: { "application/json": [".json"] },
          },
        ],
      });
      const writable = await handle.createWritable();
      await writable.write(json);
      await writable.close();
      return;
    } catch (err) {
      // User cancelled the picker → AbortError. Bail silently.
      if ((err as { name?: string })?.name === "AbortError") return;
      // Real failure → fall through to the legacy blob path so the
      // report still lands somewhere instead of being lost.
    }
  }

  // Legacy fallback. Note: most browsers auto-save to the default
  // Downloads folder without prompting — the user won't see a chooser.
  const blob = new Blob([json], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = suggestedName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Copy the report to the clipboard as JSON. Handy when the user
 *  wants to paste straight into a chat / email rather than attach a
 *  file. Returns the byte length copied so the caller can flash a
 *  confirmation. Requires the page to be the active tab (Clipboard
 *  API gating). */
export async function copyReportToClipboard(): Promise<number> {
  const report = buildReport();
  const json = JSON.stringify(report, null, 2);
  if (
    typeof navigator !== "undefined" &&
    navigator.clipboard &&
    typeof navigator.clipboard.writeText === "function"
  ) {
    await navigator.clipboard.writeText(json);
    return json.length;
  }
  throw new Error("Clipboard API not available — try Save report instead.");
}

// Test-only: lets unit tests reset state between cases without exposing
// the buffer directly. Not exported from index — call sites import it
// directly from the module.
export function _resetForTests(): void {
  buffer.length = 0;
  listeners.clear();
}
